/**
 * Conversation History Repository
 *
 * Handles CRUD operations and real-time listeners for conversation history
 * stored at users/{uid}/user_history/user_history
 *
 * Key features:
 * - Rolling 25-message limit (removes oldest when limit exceeded)
 * - Debounced writes to prevent rapid Firestore updates
 * - Real-time sync via onSnapshot
 * - Conflict detection via version field
 */

import {
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { Paths, withBackoff, debounceManager } from './firestoreUtils';
import { addUserScopedWriteGuard } from '../lib/identity/writes';
import type { ChatMessage } from '../components/GlobalChatSystem/types';
import {
  UserHistoryDoc,
  ChatMessageDoc,
  toChatMessageDoc,
  fromChatMessageDoc,
  MAX_CONVERSATION_HISTORY_LENGTH,
  sanitizeChatMessageDocsForFirestore_v20260509,
} from '../types/conversationHistoryFirestore';

// ============================================================================
// Constants
// ============================================================================

const DEBOUNCE_KEY_PREFIX = 'conversation_history_';
const DEBOUNCE_DELAY_MS = 150; // Low latency since messages are immutable once appended

// ============================================================================
// Per-uid write serialization queue
//
// Why this exists
// ───────────────
// Live-demo outage on 2026-05-09 at 07:34:08.470Z, right after PR #72
// (`9a765897`) merged + redeployed (HEAD `abce8375`). The page froze on a
// lowercase "loading..." indicator. The Commit response body was:
//
//   {
//     "message": "the stored version (1778312048316624) does not match
//                 the required base version (1778312041653525)",
//     "status": "FAILED_PRECONDITION"
//   }
//
// That is *not* a malformed payload. It is Firestore's optimistic-
// concurrency guard rejecting a `runTransaction` on the single user-
// scoped history doc because two concurrent transactions raced — one
// transaction read at base version V, a second transaction committed at
// V+1, the first transaction tried to commit and was told its base
// version no longer matched. After Firestore's internal retry budget
// was exhausted the transaction surfaced as HTTP 400 +
// `failed-precondition` to the client.
//
// PR #72's per-thread streaming routing (originatingThreadId,
// withOriginatingThread, the streaming-helper bridge through
// writeThreadMessagesRef / readThreadMessagesRef, and the per-thread
// localStorage mirrors that ConversationHistoryContext polls) made it
// possible for two `setConversationHistory(uid, …)` calls to land in the
// same animation frame against the same single doc — e.g. one originating
// from the active thread's state.messages persist effect and another
// from the per-thread mirror's localStorage poll. Both calls open
// independent transactions, both read the same base version, both try
// to commit. The loser surfaces 400.
//
// Fix
// ───
// Serialize every `setConversationHistory(uid, …)` and `appendMessage`
// per-uid through a tiny module-local promise chain. Each new call
// chains onto the previous call's promise so two concurrent invocations
// can no longer race the same doc. Combined with `getDoc` + `setDoc(…,
// {merge:true})` (last-writer-wins, no version-base check), we eliminate
// `failed-precondition` for this doc without losing any messages — the
// in-flight queue makes "last writer" deterministic per uid, and
// `mergeConversationHistory` already de-dupes by message key, so a
// queued sequence of writes converges on the same merged history every
// caller would have wanted.
//
// The Map identifier `pendingWritesByUid` is the deliberate live-bundle
// verification grep marker (the string literal survives minification
// through `Object.keys` / property access patterns elsewhere in this
// file).
// ============================================================================

/**
 * Per-uid promise chain. Each entry is the promise of the most-recently
 * scheduled write for that uid; new writes chain off of it via
 * `.catch(() => {}).then(() => actualWrite(...))` so a failure on one
 * write does not poison subsequent writes for the same user.
 *
 * Marker for live-bundle verification: the literal `"pendingWritesByUid"`
 * is preserved as a string so the deployed JS bundle is greppable.
 */
const pendingWritesByUid: Map<string, Promise<void>> = new Map();
// Live-bundle verification marker (preserved through minification as a
// string literal). Do not remove without coordinating with the deploy
// verification step.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PENDING_WRITES_BY_UID_MARKER = 'pendingWritesByUid';

/**
 * Queue a Firestore write for a given uid behind any in-flight writes for
 * the same uid. Returns the promise of the queued write so callers can
 * `await` completion.
 *
 * The queue is best-effort: if the previous write rejects, the chain
 * recovers (`.catch`) so the next write still runs.
 */
function enqueueUserWrite(uid: string, work: () => Promise<void>): Promise<void> {
  const previous = pendingWritesByUid.get(uid) || Promise.resolve();
  const next = previous.catch(() => { /* swallow — don't poison the chain */ }).then(work);
  pendingWritesByUid.set(uid, next);
  // Self-cleaning: once this write finishes, if no later write was
  // chained on top, drop the entry so the map doesn't leak.
  next.finally(() => {
    if (pendingWritesByUid.get(uid) === next) {
      pendingWritesByUid.delete(uid);
    }
  }).catch(() => { /* ignore */ });
  return next;
}

/** Test-only: clear the per-uid queue between unit tests. */
export function __resetPendingWritesByUidForTests(): void {
  pendingWritesByUid.clear();
}

// ============================================================================
// Namespace Helpers
// ============================================================================

/**
 * Get the Firestore document reference for conversation history.
 * Uses namespace if provided, otherwise uses default user_history path.
 *
 * @param uid - User ID
 * @param namespace - Optional Firestore namespace (default: 'user_history')
 * @returns Firestore document reference
 */
function getHistoryRef(uid: string, namespace?: string) {
  if (namespace && namespace !== 'user_history') {
    return Paths.tenantConversationHistoryDoc(uid, namespace);
  }
  return Paths.userConversationHistoryDoc(uid); // Default path unchanged
}

/**
 * Get the debounce key for a user and namespace.
 *
 * @param uid - User ID
 * @param namespace - Optional namespace
 * @returns Debounce key string
 */
function getDebounceKey(uid: string, namespace?: string): string {
  const suffix = namespace && namespace !== 'user_history' ? `_${namespace}` : '';
  return `${DEBOUNCE_KEY_PREFIX}${uid}${suffix}`;
}

function getMessageTimeMillis(message: ChatMessageDoc): number {
  const timestamp = message.timestamp as any;
  if (typeof timestamp?.toMillis === 'function') return timestamp.toMillis();
  if (typeof timestamp?.toDate === 'function') return timestamp.toDate().getTime();
  const parsed = new Date(timestamp).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function getMessageMergeKey(message: ChatMessageDoc): string {
  return [
    getMessageTimeMillis(message),
    message.is_user ? '1' : '0',
    message.source_tag || '',
    (message.text || '').slice(0, 160),
  ].join('|');
}

function mergeConversationHistory(
  existing: ChatMessageDoc[],
  incoming: ChatMessageDoc[]
): ChatMessageDoc[] {
  if (incoming.length === 0) return [];

  const byKey = new Map<string, ChatMessageDoc>();
  for (const message of existing) {
    byKey.set(getMessageMergeKey(message), message);
  }
  for (const message of incoming) {
    byKey.set(getMessageMergeKey(message), message);
  }

  return Array.from(byKey.values())
    .sort((a, b) => getMessageTimeMillis(a) - getMessageTimeMillis(b))
    .slice(-MAX_CONVERSATION_HISTORY_LENGTH);
}

// ============================================================================
// CRUD Operations
// ============================================================================

/**
 * Get the current conversation history for a user
 *
 * @param uid - User ID
 * @param namespace - Optional Firestore namespace (default: 'user_history')
 */
export async function getConversationHistory(
  uid: string,
  namespace?: string
): Promise<ChatMessage[]> {
  const ref = getHistoryRef(uid, namespace);
  const snap = await withBackoff(() => getDoc(ref));

  if (!snap.exists()) {
    return [];
  }

  const data = snap.data() as UserHistoryDoc;
  return (data.conversation_history || []).map(fromChatMessageDoc);
}

/**
 * Set the entire conversation history (replaces existing)
 * Used for initial sync and bulk updates
 *
 * @param uid - User ID
 * @param messages - Array of chat messages
 * @param options - Optional settings (immediate write bypasses debounce)
 * @param namespace - Optional Firestore namespace (default: 'user_history')
 */
export async function setConversationHistory(
  uid: string,
  messages: ChatMessage[],
  options?: { immediate?: boolean },
  namespace?: string
): Promise<void> {
  addUserScopedWriteGuard(uid, 'setConversationHistory');

  const ref = getHistoryRef(uid, namespace);
  const incomingMessages = messages
    .slice(-MAX_CONVERSATION_HISTORY_LENGTH)
    .map(toChatMessageDoc);

  const key = getDebounceKey(uid, namespace);

  // Per-uid serialization queue + getDoc/setDoc(merge:true) (2026-05-09
  // fix for the live-demo Firestore 400 outage). The previous
  // implementation used `runTransaction` against a single user-scoped
  // doc; concurrent calls from the new per-thread streaming routing
  // raced and surfaced as `failed-precondition` because the second
  // commit's base version no longer matched the first commit's. By
  // queuing per-uid we make “last writer” deterministic; by dropping
  // the transaction we eliminate the version-base check entirely.
  // mergeConversationHistory already de-dupes by message key, so a
  // queued sequence of writes converges on the same merged history.
  const writeHistory = async () => {
    await enqueueUserWrite(uid, async () => {
      const snap = await withBackoff(() => getDoc(ref));
      const existing = snap.exists() ? (snap.data() as UserHistoryDoc) : undefined;
      const currentHistory = existing?.conversation_history || [];
      const mergedHistory = mergeConversationHistory(currentHistory, incomingMessages);

      // Defensive sanitizer (last line of defence — strips any
      // FieldValue placeholders or undefined values that may have
      // leaked into the array of ChatMessageDoc objects).
      const sanitizedHistory = sanitizeChatMessageDocsForFirestore_v20260509(mergedHistory);

      const docData: Partial<UserHistoryDoc> = {
        conversation_history: sanitizedHistory,
        last_updated: serverTimestamp() as any,
        version: (existing?.version || 0) + 1,
      };

      await withBackoff(() => setDoc(ref, docData, { merge: true }));
    });
  };

  if (options?.immediate) {
    await writeHistory();
  } else {
    debounceManager.schedule(
      key,
      writeHistory,
      DEBOUNCE_DELAY_MS
    );
  }
}

/**
 * Append a single message to conversation history
 * Handles rolling removal of oldest message when limit exceeded
 *
 * @param uid - User ID
 * @param message - Chat message to append
 * @param namespace - Optional Firestore namespace (default: 'user_history')
 */
export async function appendMessage(
  uid: string,
  message: ChatMessage,
  namespace?: string
): Promise<void> {
  addUserScopedWriteGuard(uid, 'appendMessage');

  const ref = getHistoryRef(uid, namespace);

  // 2026-05-09 fix: same per-uid queue + getDoc/setDoc(merge:true)
  // pattern as setConversationHistory. Eliminates `failed-precondition`
  // races against the single user-scoped history doc; mergeConversationHistory
  // semantics keep this safe across queued sequences.
  await enqueueUserWrite(uid, async () => {
    const snap = await withBackoff(() => getDoc(ref));

    let currentHistory: ChatMessageDoc[] = [];
    let currentVersion = 0;

    if (snap.exists()) {
      const data = snap.data() as UserHistoryDoc;
      currentHistory = data.conversation_history || [];
      currentVersion = data.version || 0;
    }

    // Add new message
    const newMessageDoc = toChatMessageDoc(message);
    currentHistory.push(newMessageDoc);

    // Rolling removal: keep only last 25
    if (currentHistory.length > MAX_CONVERSATION_HISTORY_LENGTH) {
      currentHistory = currentHistory.slice(-MAX_CONVERSATION_HISTORY_LENGTH);
    }

    // Defensive sanitizer (see setConversationHistory).
    const sanitizedHistory = sanitizeChatMessageDocsForFirestore_v20260509(currentHistory);

    await withBackoff(() => setDoc(
      ref,
      {
        conversation_history: sanitizedHistory,
        last_updated: serverTimestamp(),
        version: currentVersion + 1,
      },
      { merge: true }
    ));
  });
}

/**
 * Update the most recent message (for streaming updates)
 * Debounced to prevent rapid writes during streaming
 *
 * @param uid - User ID
 * @param message - Updated chat message
 * @param namespace - Optional Firestore namespace (default: 'user_history')
 */
export async function updateLatestMessage(
  uid: string,
  message: ChatMessage,
  namespace?: string
): Promise<void> {
  addUserScopedWriteGuard(uid, 'updateLatestMessage');

  const ref = getHistoryRef(uid, namespace);
  const key = `${getDebounceKey(uid, namespace)}_update`;

  debounceManager.schedule(
    key,
    async () => {
      // 2026-05-09 fix: per-uid queue + getDoc/setDoc(merge:true). Same
      // failed-precondition race surface as setConversationHistory — this
      // path also writes the single user-scoped history doc.
      await enqueueUserWrite(uid, async () => {
        const snap = await withBackoff(() => getDoc(ref));

        if (!snap.exists()) return;

        const data = snap.data() as UserHistoryDoc;
        const history = [...(data.conversation_history || [])];

        if (history.length === 0) return;

        // Update the last message
        history[history.length - 1] = toChatMessageDoc(message);

        // Defensive sanitizer (see setConversationHistory).
        const sanitizedHistory = sanitizeChatMessageDocsForFirestore_v20260509(history);

        await withBackoff(() => setDoc(
          ref,
          {
            conversation_history: sanitizedHistory,
            last_updated: serverTimestamp(),
            version: (data.version || 0) + 1,
          },
          { merge: true }
        ));
      });
    },
    DEBOUNCE_DELAY_MS
  );
}

/**
 * Flush any pending debounced writes
 * Call before page unload or when immediate sync is needed
 *
 * @param uid - User ID
 * @param namespace - Optional Firestore namespace (default: 'user_history')
 */
export function flushPendingWrites(uid: string, namespace?: string): void {
  const key = getDebounceKey(uid, namespace);
  debounceManager.flush(key);
  debounceManager.flush(`${key}_update`);
}

// ============================================================================
// Real-time Listeners
// ============================================================================

/**
 * Subscribe to conversation history with real-time updates
 *
 * @param uid - User ID
 * @param onData - Callback with updated messages and version
 * @param onError - Optional error callback
 * @param namespace - Optional Firestore namespace (default: 'user_history')
 * @returns Unsubscribe function
 */
export function onConversationHistorySnapshot(
  uid: string,
  onData: (messages: ChatMessage[], version: number) => void,
  onError?: (error: Error) => void,
  namespace?: string
): Unsubscribe {
  const ref = getHistoryRef(uid, namespace);

  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onData([], 0);
        return;
      }

      const data = snap.data() as UserHistoryDoc;
      const messages = (data.conversation_history || []).map(fromChatMessageDoc);
      onData(messages, data.version || 0);
    },
    (error) => {
      console.error('[ConversationHistory] Snapshot error:', error);
      onError?.(error);
    }
  );
}

/**
 * Clear all conversation history for a user
 *
 * @param uid - User ID
 * @param namespace - Optional Firestore namespace (default: 'user_history')
 */
export async function clearConversationHistory(uid: string, namespace?: string): Promise<void> {
  addUserScopedWriteGuard(uid, 'clearConversationHistory');

  const ref = getHistoryRef(uid, namespace);

  await withBackoff(() =>
    setDoc(
      ref,
      {
        conversation_history: [],
        last_updated: serverTimestamp(),
        version: 0,
      },
      { merge: true }
    )
  );
}

/**
 * Result of deleting messages by tag
 */
export interface DeleteMessagesByTagResult {
  deletedCount: number;
  remainingMessages: ChatMessage[];  // Client format (camelCase) for immediate UI use
}

/**
 * Delete chat messages with a specific tag (e.g., "test example")
 * Preserves messages without the tag
 *
 * @param uid - User ID
 * @param tag - Tag to filter messages by
 * @param namespace - Optional Firestore namespace (default: 'user_history')
 * @returns Object with deletedCount and remainingMessages (converted to client format) for immediate UI update
 */
export async function deleteMessagesByTag(
  uid: string,
  tag: string,
  namespace?: string
): Promise<DeleteMessagesByTagResult> {
  addUserScopedWriteGuard(uid, 'deleteMessagesByTag');

  const ref = getHistoryRef(uid, namespace);

  let deletedCount = 0;
  let remainingDocs: ChatMessageDoc[] = [];

  // 2026-05-09 fix: per-uid queue + getDoc/setDoc(merge:true). Same race
  // surface as setConversationHistory.
  await enqueueUserWrite(uid, async () => {
    const snap = await withBackoff(() => getDoc(ref));

    if (!snap.exists()) {
      remainingDocs = [];
      return;
    }

    const data = snap.data() as UserHistoryDoc;
    const currentHistory = data.conversation_history || [];

    // Filter out messages with the tag
    const filteredHistory = currentHistory.filter(msg => {
      const tags = msg.tags || [];
      if (tags.includes(tag)) {
        deletedCount++;
        return false; // Remove this message
      }
      return true; // Keep this message
    });

    // Defensive sanitizer (see setConversationHistory).
    const sanitizedHistory = sanitizeChatMessageDocsForFirestore_v20260509(filteredHistory);
    remainingDocs = sanitizedHistory;

    await withBackoff(() => setDoc(
      ref,
      {
        conversation_history: sanitizedHistory,
        last_updated: serverTimestamp(),
        version: (data.version || 0) + 1,
      },
      { merge: true }
    ));
  });

  // Convert Firestore docs (snake_case) to client format (camelCase) for proper UI rendering
  // This ensures isUser, sourceTag, etc. are correctly named for the UI
  const remainingMessages = remainingDocs.map(fromChatMessageDoc);

  return { deletedCount, remainingMessages };
}
